/**
 * redactionStreamHub — **l'écoute d'une rédaction appartient au travail, pas à
 * l'écran** (#857).
 *
 * ## Pourquoi ce service ne vit pas dans React
 *
 * Le texte d'une rédaction ne passe pas par la connexion temps réel. Il descend
 * par un flux HTTP que le navigateur ouvre lui-même (`/api/jobs/:id/stream`),
 * **sans tampon serveur ni reprise possible** : la grâce de 180 s et le rejeu
 * qui protègent le chat n'ont aucun équivalent ici. Ce qui n'est pas lu au
 * moment où il descend est perdu pour de bon.
 *
 * Tant que la lecture vivait dans un effet React, elle avait deux propriétés
 * fatales, toutes deux héritées du cycle de vie d'un composant :
 *  - le **nettoyage d'effet annulait le flux** — un démontage devenait une
 *    annulation de rédaction, ce qu'aucun avocat n'a demandé ;
 *  - un **remontage rouvrait le flux**, et rouvrir ne rejoue rien : le serveur
 *    **relance le modèle**. Deux générations en vol pour une seule demande,
 *    écrivant dans le même document, facturées deux fois.
 *
 * Ce second point est le cœur du ticket, et la raison pour laquelle il interdit
 * d'extraire la garde d'attachement : réparer la survie sans elle serait pire
 * que le défaut d'origine. Aujourd'hui, chaque retour sur l'assistant rouvrait
 * bien le tuyau — inoffensif par accident seulement, parce que le travail venait
 * d'être marqué en échec et que le serveur refusait.
 *
 * La règle n'est donc pas un refus mais un **attachement** : une écoute par
 * travail, et qui la demande la rejoint. L'avocat qui revient doit voir sa
 * rédaction s'écrire, pas en déclencher une seconde.
 *
 * ## Le patron est celui du pool de connexions
 *
 * Instance de module, constructeur privé, aucune dépendance à React ni à
 * Zustand, indexation par identité métier (ici l'identifiant du travail) et
 * `ecouter()` idempotent — exactement `WebSocketPool.openSession`, qui rend la
 * clé existante quand la conversation est déjà servie. L'option écartée, et qui
 * doit le rester, est le rattachement à la coque en code React : ça marche tant
 * que personne ne touche à la coque, c'est-à-dire jusqu'au prochain #845.
 *
 * ## L'annonce de fin part sur la conversation DU TRAVAIL
 *
 * Chaque écoute retient la conversation qui a lancé le travail — celle sur
 * laquelle la trame `redaction_job_started` est arrivée — et jamais celle qui se
 * trouve affichée quand le flux se termine. Les deux divergent dès que l'avocat
 * change de fil, et le backend attend cette annonce huit minutes
 * (`RedactionJobCollector.waitForSseComplete`) avant d'abandonner : adressée au
 * mauvais fil, l'agent ne reprend jamais la parole sur celui qui a demandé
 * l'acte.
 *
 * Le service ne connaît pas le transport : il appelle un notificateur que le
 * pool lui pose (`definirNotificateurDeFin`). C'est ce qui lui permet de ne rien
 * importer du chat, et donc de ne jamais former de cycle avec lui.
 */

import { StructuralParser, type StructuredBlock } from '@hector/shared-types';
import { getAuthToken } from '@/lib/apiClient';
import { canvasDocumentRuntime } from '../state/canvasDocumentRuntime';

/** Les deux flux de rédaction, distingués par leur endpoint. */
export type RedactionStreamKind = 'research' | 'generation';

/** Ce que le backend attend comme verdict de fin de flux. */
export type RedactionFinStatut = 'complete' | 'error' | 'cancelled';

/** Où en est une écoute. */
export type RedactionEcouteStatut = 'en-cours' | 'terminee' | 'echouee' | 'annulee';

/** L'état d'une écoute, tel qu'un affichage peut le lire à tout moment. */
export interface RedactionEcoute {
  readonly jobId: string;
  readonly kind: RedactionStreamKind;
  /**
   * La conversation qui a lancé le travail. Jamais réécrite.
   *
   * ⚠️ **C'est aussi l'identité du DOCUMENT** (#1336). L'écoute portait en plus
   * un `documentSessionId` acquis avant l'ouverture du flux ; la conversation
   * étant devenue la clé du document logique, le conserver serait un second nom
   * du même fait. L'écoute ne porte pas d'époque de montage : elle vit hors de
   * React et survit délibérément aux remontages (#857).
   */
  readonly conversationId: string;
  /** Nom choisi dans le parcours de lancement ; l'IA ne le remplace jamais. */
  readonly documentTitle?: string | null;
  /** Les blocs déjà produits, dans l'ordre. Remplacés (jamais mutés) à chaque ajout. */
  readonly blocs: readonly StructuredBlock[];
  /** Le raisonnement du modèle, accumulé. */
  readonly raisonnement: string;
  /** Le modèle a-t-il commencé à écrire le contenu (fin de la phase de réflexion) ? */
  readonly contenuCommence: boolean;
  readonly statut: RedactionEcouteStatut;
}

/** Ce qu'il faut pour démarrer — ou rejoindre — une écoute. */
export interface DemandeDEcoute {
  kind: RedactionStreamKind;
  jobId: string;
  conversationId: string;
  /** Nom déjà validé par l'avocat avant l'enqueue du job. */
  documentTitle?: string;
  /** Epoch captured at admission; makes completion callbacks reject a remounted scope. */
  mountEpoch?: number;
}

/** Résultat synchrone de la liaison, obtenu avant toute consommation réseau. */
export type DecisionDEcoute =
  | { statut: 'demarree' | 'rejointe'; jobId: string }
  | { statut: 'refusee'; jobId: string; raison: string }
  | {
      statut: 'conflit';
      jobId: string;
      liaisonExistante: { conversationId: string; mountEpoch: number | undefined };
    };

/** Liaison immuable d'un travail, qu'il ouvre un flux HTTP ou écrive en un lot. */
export type LiaisonDeTravail = Readonly<DemandeDEcoute>;

export type DecisionDeLiaison =
  | { statut: 'liee' | 'rejointe'; jobId: string }
  | Extract<DecisionDEcoute, { statut: 'conflit' | 'refusee' }>;

/** Décision pure du bouton Stop face à un callback React potentiellement périmé. */
export type DecisionDArretDuTravailAffiche =
  | { statut: 'arreter'; jobId: string | null }
  | { statut: 'ignore'; raison: 'rendu-perime' };

export function deciderArretDuTravailAffiche(input: {
  jobIdAffiche: string | null;
  dernierJobIdDeLaSession: string | null;
}): DecisionDArretDuTravailAffiche {
  if (
    input.jobIdAffiche !== null
    && input.dernierJobIdDeLaSession !== input.jobIdAffiche
  ) {
    return { statut: 'ignore', raison: 'rendu-perime' };
  }
  return { statut: 'arreter', jobId: input.jobIdAffiche };
}

/** Prévient le transport qu'un travail est terminé, sur SA conversation. */
export type NotificateurDeFin = (
  conversationId: string,
  jobId: string,
  statut: RedactionFinStatut,
) => void;

interface EcouteInterne extends RedactionEcoute {
  blocs: StructuredBlock[];
  raisonnement: string;
  contenuCommence: boolean;
  statut: RedactionEcouteStatut;
  /** De quoi couper le flux — jamais appelé par un démontage. */
  abandon: AbortController;
  /**
   * Vide le tampon du parseur. Posé dès que le parseur existe, appelé sur
   * **toutes** les fins — voir `conclure`.
   */
  finaliserLeParseur: () => void;
}

interface SSEEvent {
  type: string;
  content?: string;
}

/**
 * Vite ne relaie pas `/api/*` vers le backend : une URL relative taperait le
 * serveur de développement et rendrait du HTML. On préfixe donc l'origine
 * backend, avec les mêmes variables qu'`apiClient`.
 */
function urlDuFlux(kind: RedactionStreamKind, jobId: string): string {
  const chemin =
    kind === 'research'
      ? `/api/jobs/${jobId}/stream-plan-detaille`
      : `/api/jobs/${jobId}/stream`;
  const apiBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (apiBase) return `${apiBase.replace(/\/api\/?$/, '')}${chemin}`;
  return `${(import.meta.env.VITE_BACKEND_URL as string | undefined) ?? ''}${chemin}`;
}

/** La clé d'une écoute : l'identifiant du travail, et lui seul. */
type CleDEcoute = string;

class RedactionStreamHub {
  private ecoutes = new Map<CleDEcoute, EcouteInterne>();
  /**
   * La liaison précède et dépasse l'écoute : une génération orchestrateur
   * écrit en un lot sans ouvrir de flux HTTP, mais doit être fenced exactement
   * comme une génération streamée.
   */
  private liaisons = new Map<CleDEcoute, LiaisonDeTravail>();
  private abonnes = new Set<(jobId: string) => void>();
  private notifier: NotificateurDeFin | null = null;
  private jobProtections = new Map<string, {
    releasePin: () => void;
    releaseActivity: () => void;
  }>();

  /**
   * Poser le transport. Appelé une fois par le pool de connexions, qui sait
   * seul retrouver la connexion d'une conversation au moment où le flux
   * s'achève — et non au moment où il a démarré, ce qui laisserait l'annonce
   * partir sur une session morte après une reconnexion.
   */
  definirNotificateurDeFin(notifier: NotificateurDeFin): void {
    this.notifier = notifier;
  }

  /**
   * Écouter un travail — ou rejoindre l'écoute déjà en cours.
   *
   * **Idempotent, et c'est tout l'objet du ticket.** Appelé pour un travail
   * déjà écouté, terminé, échoué ou annulé, il ne rouvre rien : un travail ne
   * se réécoute pas, il se rejoint. L'identifiant d'un travail survit à la fin
   * de son flux côté affichage, donc sans cette garde le moindre remontage
   * relancerait une génération complète pour un acte déjà écrit.
   */
  ecouter(demande: DemandeDEcoute): DecisionDEcoute {
    const { kind, jobId, conversationId } = demande;
    const liaison = this.lierTravail(demande);
    if (liaison.statut === 'conflit' || liaison.statut === 'refusee') return liaison;
    const existante = this.ecoutes.get(jobId);
    if (existante) {
      // Une seule exception à l'idempotence, et elle est étroite : une écoute
      // qui a échoué **sans avoir reçu un seul bloc** n'a rien lancé du tout
      // (jeton d'authentification absent, ouverture refusée, coupure avant le
      // premier mot). Rien n'est en vol, donc réessayer ne peut pas mettre deux
      // générations en parallèle — alors qu'un refus définitif ferait de
      // l'incident réseau d'une seconde une condamnation à vie pour ce travail.
      // Dès qu'un bloc est arrivé, le modèle a tourné : on ne rouvre plus.
      if (existante.statut !== 'echouee' || existante.blocs.length > 0) {
        return { statut: 'rejointe', jobId };
      }
      console.warn(
        `[redaction] nouvelle tentative d'écoute du job ${jobId} — la précédente a échoué `
        + 'avant le premier bloc, donc aucune génération n\'est en vol.',
      );
      this.ecoutes.delete(jobId);
    }

    const ecoute: EcouteInterne = {
      jobId,
      kind,
      conversationId,
      documentTitle: demande.documentTitle?.trim() || null,
      blocs: [],
      raisonnement: '',
      contenuCommence: false,
      statut: 'en-cours',
      abandon: new AbortController(),
      finaliserLeParseur: () => undefined,
    };
    let releasePin: (() => void) | null = null;
    try {
      releasePin = canvasDocumentRuntime.pinSession(conversationId, { kind: 'job', id: jobId });
    } catch (error) {
      return this.refuserProtection(demande, 'job pin could not be acquired', error);
    }
    const releaseActivity = canvasDocumentRuntime.tryBeginActivity(conversationId, 'stream');
    if (!releaseActivity) {
      releasePin();
      return this.refuserProtection(demande, 'stream activity could not be acquired');
    }
    this.jobProtections.set(jobId, { releasePin, releaseActivity });
    this.ecoutes.set(jobId, ecoute);
    // Réveiller AVANT le premier bloc, et c'est load-bearing depuis #859 : le
    // bandeau « une rédaction est en cours » doit s'allumer au lancement du
    // travail. Attendre le premier bloc le laisserait éteint pendant toute la
    // phase de raisonnement du modèle — soit précisément le moment où l'avocat
    // change d'écran en croyant que rien n'a démarré.
    this.prevenir(jobId);
    void this.lireLeFlux(ecoute);
    return { statut: 'demarree', jobId };
  }

  /**
   * Fige l'identité documentaire AVANT tout flux ou écriture.
   *
   * Ce registre est volontairement distinct de `ecoutes` : le job de rendu
   * orchestrateur arrive déjà terminé et ne doit surtout pas ouvrir le flux
   * `/api/jobs/:id/stream`, qui relancerait le modèle.
   */
  lierTravail(demande: DemandeDEcoute): DecisionDeLiaison {
    // ⚠️ Ce qui reste de l'ancienne garde de propriété, c'est l'EXISTENCE, pas
    // la comparaison (#1336). Elle confrontait la session enregistrée pour la
    // conversation à celle que la demande annonçait ; la conversation étant
    // désormais la clé du document logique, cette confrontation rendrait la même
    // valeur avec le défaut et sans lui. Reste la question qui tranche : cette
    // conversation possède-t-elle un document ? Un travail lié à un fil sans
    // document épinglerait dans le vide, et son flux écrirait nulle part.
    //
    // La lecture est REGISTRE, pas directory : `getSessionForConversation`
    // répond « ce document est-il monté ? », or un travail se lie avant tout
    // montage physique — s'y fier refuserait toute génération lancée depuis un
    // fil dont le canvas n'est pas encore construit.
    const possedeUnDocument = canvasDocumentRuntime.getSessionsSnapshot().sessions
      .some((session) => session.conversationId === demande.conversationId);
    if (!possedeUnDocument) {
      return this.refuserProtection(
        demande,
        'conversation owns no canvas document session in the registry',
      );
    }
    const existante = this.liaisons.get(demande.jobId);
    if (!existante) {
      this.liaisons.set(demande.jobId, Object.freeze({ ...demande }));
      return { statut: 'liee', jobId: demande.jobId };
    }
    if (
      existante.kind === demande.kind
      && existante.conversationId === demande.conversationId
      // L'époque est comparée EN PLUS de la conversation, jamais seule : c'est
      // elle qui distingue le montage capturé à l'admission d'un remontage
      // survenu depuis, et la conversation qui empêche de rapprocher deux
      // documents sur la seule valeur numérique d'un compteur d'onglet.
      && existante.mountEpoch === demande.mountEpoch
    ) {
      return { statut: 'rejointe', jobId: demande.jobId };
    }

    console.error(
      `[redaction] liaison refusée pour le job ${demande.jobId} : il appartient déjà au montage `
      + `${existante.conversationId}#${existante.mountEpoch ?? 'sans époque'} (${existante.kind}); `
      + `demande reçue pour ${demande.conversationId}#${demande.mountEpoch ?? 'sans époque'} `
      + `(${demande.kind}).`,
    );
    return {
      statut: 'conflit',
      jobId: demande.jobId,
      liaisonExistante: {
        conversationId: existante.conversationId,
        mountEpoch: existante.mountEpoch,
      },
    };
  }

  /** Identité figée d'un travail, sans exposer l'objet conservé par le hub. */
  lireLiaison(jobId: string): LiaisonDeTravail | null {
    const liaison = this.liaisons.get(jobId);
    return liaison ? { ...liaison } : null;
  }

  /**
   * Les conversations qui rédigent en ce moment, sans doublon, dans l'ordre où
   * leurs travaux ont été lancés.
   *
   * **Grossier à dessein.** Le hub réveille ses abonnés à chaque bloc, mais
   * cette réponse ne change qu'au démarrage et à la fin d'un travail : un
   * afficheur qui la compare avant de poser son état ne re-rend donc pas au
   * rythme du flux. C'est la condition que #859 impose à la liste des
   * conversations, et le même patron que `useSessionBadges` pour le store des
   * sessions, qui mute à ~50 Hz.
   *
   * Une conversation peut porter deux travaux (le plan, puis l'acte lancé dans
   * la foulée) : elle n'apparaît qu'une fois, parce que ce qu'on montre à
   * l'avocat est « ce fil rédige », pas un décompte de travaux.
   */
  conversationsQuiRedigent(): string[] {
    const vues = new Set<string>();
    for (const ecoute of this.ecoutes.values()) {
      if (ecoute.statut === 'en-cours') vues.add(ecoute.conversationId);
    }
    return [...vues];
  }

  /** L'état d'une écoute, ou `null` si ce travail n'a jamais été écouté. */
  lire(jobId: string): RedactionEcoute | null {
    return this.ecoutes.get(jobId) ?? null;
  }

  /** Les travaux en cours d'écriture pour cette conversation. */
  travauxEnCoursPour(conversationId: string | null): string[] {
    if (!conversationId) return [];
    const enCours: string[] = [];
    for (const ecoute of this.ecoutes.values()) {
      if (ecoute.statut === 'en-cours' && ecoute.conversationId === conversationId) {
        enCours.push(ecoute.jobId);
      }
    }
    return enCours;
  }

  /**
   * Dernier travail lié à ce document, y compris terminé pour permettre la
   * reprise.
   *
   * ⚠️ **Adressé par la CONVERSATION** (#1336), et c'est la même clé que
   * `conversationsQuiRedigent` / `travauxEnCoursPour`. Cette lecture prenait
   * auparavant une session documentaire, ce qui obligeait chaque appelant à
   * traduire d'abord sa conversation par le registre — une traduction que la
   * PREMIÈRE notification du hub précède, d'où des réveils sans rien à montrer.
   */
  derniereEcoutePourConversation(conversationId: string | null): RedactionEcoute | null {
    if (!conversationId) return null;
    let derniere: RedactionEcoute | null = null;
    for (const ecoute of this.ecoutes.values()) {
      if (ecoute.conversationId === conversationId) derniere = ecoute;
    }
    return derniere;
  }

  /** Une écoute est-elle en cours pour cette conversation ? */
  ecouteEnCoursPour(conversationId: string | null): boolean {
    return this.travauxEnCoursPour(conversationId).length > 0;
  }

  /**
   * Être prévenu qu'une écoute a changé. Se désabonner ne coupe **rien** —
   * c'est exactement ce qui distingue ce service d'un effet React.
   */
  souscrire(abonne: (jobId: string) => void): () => void {
    this.abonnes.add(abonne);
    return () => {
      this.abonnes.delete(abonne);
    };
  }

  /**
   * Arrêter une écoute sur un geste explicite. La fin est **annoncée** : sans
   * elle, le backend attend son signal huit minutes et l'agent ne reprend
   * jamais la parole.
   */
  annuler(jobId: string, raison: string): void {
    const ecoute = this.ecoutes.get(jobId);
    if (!ecoute || ecoute.statut !== 'en-cours') return;
    console.log(`[redaction] écoute annulée — job ${jobId} (${raison})`);
    this.conclure(ecoute, 'annulee', 'cancelled');
  }

  /**
   * Tout abandonner, sans rien annoncer — au changement de compte.
   *
   * Rien n'est notifié à dessein : la connexion de l'avocat qui part ne portera
   * plus aucune trame, et l'avocat suivant n'a pas à recevoir la fin d'un
   * travail qu'il n'a pas demandé. Les écoutes sont **effacées** et non
   * conclues : leur contenu est l'acte de quelqu'un d'autre.
   */
  abandonnerTout(raison: string): void {
    if (this.ecoutes.size === 0 && this.liaisons.size === 0) return;
    console.log(`[redaction] ${this.ecoutes.size} écoute(s) abandonnée(s) — ${raison}`);
    for (const ecoute of this.ecoutes.values()) ecoute.abandon.abort();
    for (const protection of this.jobProtections.values()) {
      protection.releaseActivity();
      protection.releasePin();
    }
    this.jobProtections.clear();
    const abandonnes = [...this.ecoutes.keys()];
    this.ecoutes.clear();
    this.liaisons.clear();
    for (const jobId of abandonnes) this.prevenir(jobId);
  }

  /** Remise à zéro complète — rechargement à chaud et tests. */
  detruireTout(): void {
    for (const ecoute of this.ecoutes.values()) ecoute.abandon.abort();
    for (const protection of this.jobProtections.values()) {
      protection.releaseActivity();
      protection.releasePin();
    }
    this.jobProtections.clear();
    this.ecoutes.clear();
    this.liaisons.clear();
    this.abonnes.clear();
    this.notifier = null;
  }

  private prevenir(jobId: string): void {
    for (const abonne of [...this.abonnes]) abonne(jobId);
  }

  /**
   * Fin unique d'une écoute : vidage du tampon, statut, annonce au transport,
   * réveil des abonnés.
   *
   * **Le vidage passe en premier, et sur toutes les fins sans exception.** Le
   * parseur retient le paragraphe en cours et, pour un tableau, ses lignes
   * jusqu'à la dernière : sans `finalize()`, une annulation ou une trame
   * d'erreur jetterait tout ce qui est déjà descendu mais pas encore émis en
   * bloc. C'est exactement ce que promet « annuler conserve » (#855), et
   * l'enregistrement du brouillon suit l'arrêt de si près qu'il figerait l'acte
   * amputé. L'ancien code appelait `finalize()` sur ses trois issues ; le
   * perdre ici était une régression silencieuse.
   */
  private conclure(
    ecoute: EcouteInterne,
    statut: RedactionEcouteStatut,
    verdict: RedactionFinStatut,
  ): void {
    if (ecoute.statut !== 'en-cours') return;
    try { ecoute.finaliserLeParseur(); } catch { /* un tampon illisible ne doit pas retenir la fin */ }
    ecoute.statut = statut;
    ecoute.abandon.abort();
    const protection = this.jobProtections.get(ecoute.jobId);
    protection?.releaseActivity();
    protection?.releasePin();
    this.jobProtections.delete(ecoute.jobId);
    this.notifier?.(ecoute.conversationId, ecoute.jobId, verdict);
    this.prevenir(ecoute.jobId);
  }

  private refuserProtection(
    demande: DemandeDEcoute,
    cause: string,
    error?: unknown,
  ): Extract<DecisionDEcoute, { statut: 'refusee' }> {
    const detail = error instanceof Error ? ` Detail: ${error.message}` : '';
    const raison = `Redaction job ${demande.jobId} refused for conversationId=`
      + `${demande.conversationId} mountEpoch=${demande.mountEpoch ?? 'none'}: ${cause}.`
      + `${detail} Acquire the canvas document of this conversation before retrying.`;
    console.error(`[redaction] ${raison}`);
    return { statut: 'refusee', jobId: demande.jobId, raison };
  }

  /**
   * La lecture du flux. Découpage manuel des trames `data:` : c'est un `fetch`
   * et non un `EventSource`, faute de pouvoir poser un en-tête d'autorisation
   * sur ce dernier.
   */
  private async lireLeFlux(ecoute: EcouteInterne): Promise<void> {
    const parseur = new StructuralParser({
      onBlock: (bloc) => {
        // Remplacé, jamais muté : l'affichage compare les tableaux par identité
        // pour n'insérer que les blocs nouveaux dans l'éditeur.
        ecoute.blocs = [...ecoute.blocs, bloc];
        this.prevenir(ecoute.jobId);
      },
    });
    ecoute.finaliserLeParseur = () => parseur.finalize();

    const traiter = (donnee: string): void => {
      // Une annulation peut gagner pendant que `reader.read()` attend déjà un
      // paquet réseau. Ce paquet appartient au job terminé : le parseur ne doit
      // jamais le republier dans le document après le terminal unique.
      if (ecoute.statut !== 'en-cours') return;
      const propre = donnee.trim();
      if (!propre || propre === '[DONE]') return;
      try {
        const trame = JSON.parse(propre) as SSEEvent;
        if (trame.type === 'token' && typeof trame.content === 'string') {
          if (!ecoute.contenuCommence) {
            ecoute.contenuCommence = true;
            this.prevenir(ecoute.jobId);
          }
          parseur.push(trame.content);
        } else if (trame.type === 'thinking' && typeof trame.content === 'string') {
          ecoute.raisonnement += trame.content;
          this.prevenir(ecoute.jobId);
        } else if (trame.type === 'complete') {
          this.conclure(ecoute, 'terminee', 'complete');
        } else if (trame.type === 'error' || trame.type === 'job_failed') {
          this.conclure(ecoute, 'echouee', 'error');
        }
        // Les autres trames (`connection`, battements) sont du service.
      } catch {
        // Donnée non-JSON : le backend n'en émet pas, mais la perdre serait pire
        // que la pousser telle quelle au parseur.
        try { parseur.push(donnee); } catch { /* ignore */ }
      }
    };

    try {
      const jeton = await getAuthToken();
      if (!jeton) {
        console.error(
          `[redaction] écoute impossible — aucun jeton d'authentification pour le job `
          + `${ecoute.jobId} (conversation ${ecoute.conversationId}). L'acte ne s'écrira pas.`,
        );
        this.conclure(ecoute, 'echouee', 'error');
        return;
      }
      const reponse = await fetch(urlDuFlux(ecoute.kind, ecoute.jobId), {
        method: 'GET',
        signal: ecoute.abandon.signal,
        headers: {
          Authorization: `Bearer ${jeton}`,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
      });
      if (!reponse.ok || !reponse.body) {
        console.error(
          `[redaction] le flux du job ${ecoute.jobId} a été refusé (HTTP ${reponse.status}, `
          + `conversation ${ecoute.conversationId}).`,
        );
        this.conclure(ecoute, 'echouee', 'error');
        return;
      }

      const lecteur = reponse.body.getReader();
      const decodeur = new TextDecoder('utf-8');
      let tampon = '';
      while (ecoute.statut === 'en-cours') {
        const { done, value } = await lecteur.read();
        if (ecoute.statut !== 'en-cours') return;
        if (done) {
          // Fin de flux sans trame de conclusion : le backend interrompu après
          // avoir écrit ferme proprement. On garde ce qui est arrivé.
          if (tampon.trim()) {
            for (const ligne of tampon.split('\n')) {
              if (ligne.startsWith('data:')) traiter(ligne.slice(5));
            }
          }
          this.conclure(ecoute, 'terminee', 'complete');
          return;
        }
        tampon += decodeur.decode(value, { stream: true });
        // Une trame se termine à la double fin de ligne. Un vrai flux se coupe
        // sur des frontières réseau arbitraires, y compris au milieu d'un
        // caractère accentué : d'où le décodage incrémental et ce tampon.
        let coupure: number;
        while ((coupure = tampon.indexOf('\n\n')) >= 0) {
          const trame = tampon.slice(0, coupure);
          tampon = tampon.slice(coupure + 2);
          for (const ligne of trame.split('\n')) {
            if (ligne.startsWith('data:')) traiter(ligne.slice(5));
          }
        }
      }
    } catch (err) {
      if ((err as { name?: string } | null)?.name === 'AbortError') return;
      console.error(
        `[redaction] le flux du job ${ecoute.jobId} (conversation ${ecoute.conversationId}) `
        + `s'est rompu : ${(err as Error | null)?.message ?? 'cause inconnue'}`,
      );
      this.conclure(ecoute, 'echouee', 'error');
    }
  }
}

/**
 * L'instance unique. Comme le pool de connexions, elle vit dans le module :
 * c'est ce qui la rend indépendante de tout arbre React.
 */
export const redactionStreamHub = new RedactionStreamHub();
